import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Cake, Heart, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/calendar/events").then((r) => setEvents(r.data));
  }, []);

  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startOffset = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsForDay = (d) => {
    if (!d) return [];
    return events.filter((e) => {
      if (!e.date) return false;
      const [ , em, ed] = e.date.split("-").map(Number);
      return em - 1 === m && ed === d;
    });
  };

  const monthEvents = events
    .filter((e) => {
      if (!e.date) return false;
      const [ , em] = e.date.split("-").map(Number);
      return em - 1 === m;
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-8" data-testid="calendar-page">
      <div>
        <h1 className="font-display text-4xl text-charcoal">Calendar</h1>
        <p className="text-charcoal/60 mt-2">A quiet map of the year's moments.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface p-6">
          <div className="flex items-center justify-between mb-6">
            <p className="font-display text-2xl text-charcoal">{MONTHS[m]} <span className="text-charcoal/40">{y}</span></p>
            <div className="flex gap-2">
              <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="rounded-full p-2 hover:bg-linen" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setCursor(new Date())} className="rounded-full px-3 py-1 text-xs font-semibold hover:bg-linen border border-warm" data-testid="cal-today">Today</button>
              <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="rounded-full p-2 hover:bg-linen" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map((d, i) => <div key={i} className="text-center text-xs uppercase tracking-widest text-charcoal/40 py-2">{d}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              const evs = eventsForDay(d);
              const today = d && new Date().toDateString() === new Date(y, m, d).toDateString();
              return (
                <div key={i} className={`aspect-square rounded-xl p-1.5 text-sm relative ${d ? "bg-linen-2 hover:bg-linen" : ""} ${today ? "ring-2 ring-terracotta" : ""}`}>
                  {d && (
                    <>
                      <span className={`${today ? "text-terracotta font-semibold" : "text-charcoal/70"}`}>{d}</span>
                      <div className="absolute bottom-1.5 left-1.5 right-1.5 flex flex-col gap-0.5">
                        {evs.slice(0, 2).map((e) => (
                          <button key={`${e.relationship_id}-${e.occasion}`} onClick={() => navigate(`/relationships/${e.relationship_id}`)}
                            data-testid={`cal-event-${e.relationship_id}-${e.occasion}`}
                            className={`text-[10px] truncate rounded-full px-1.5 py-0.5 text-white ${e.occasion === "anniversary" ? "bg-sage" : "bg-terracotta"}`}>
                            {e.name}
                          </button>
                        ))}
                        {evs.length > 2 && <span className="text-[9px] text-charcoal/50">+{evs.length - 2}</span>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="card-surface p-6">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-4">This month</p>
          {monthEvents.length === 0 ? (
            <p className="text-sm text-charcoal/60">Nothing scheduled.</p>
          ) : (
            <div className="space-y-3">
              {monthEvents.map((e, i) => (
                <motion.button key={`${e.relationship_id}-${e.occasion}`} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  onClick={() => navigate(`/relationships/${e.relationship_id}`)} data-testid={`month-event-${i}`}
                  className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-linen text-left">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${e.occasion === "anniversary" ? "bg-sage/20" : "bg-terracotta/15"}`}>
                    {e.occasion === "anniversary" ? <Heart className="w-4 h-4 sage" strokeWidth={1.5} /> : <Cake className="w-4 h-4 text-terracotta" strokeWidth={1.5} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-charcoal truncate">{e.name}</p>
                    <p className="text-xs text-charcoal/60">{e.occasion} · {e.date}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
